import React, { useState } from "react";
import { FileText, TrendingUp, Receipt, Landmark, Timer, Umbrella, Calendar, UserCheck, Folder, Bell, Shield, Smartphone } from "lucide-react";
import { BG_SOFT, BRAND, TEXT_INK, TEXT_DARK, CONTAINER } from "../tokens";

interface Feature {
  icon: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  title: string;
  note?: string;
  comingSoon?: boolean;
}

interface FeatureCardProps { feature: Feature; }

function FeatureCard({ feature }: FeatureCardProps) {
  const [hovered, setHovered] = useState(false);
  const Icon = feature.icon;
  const muted = feature.comingSoon;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#FFFFFF",
        border: "0.5px solid #EAF0F0",
        borderRadius: 18,
        padding: 26,
        position: "relative",
        boxShadow: hovered ? "0 22px 48px rgba(8,32,31,0.14)" : "0 1px 2px rgba(8,32,31,0.04)",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        transition: "transform 0.28s ease, box-shadow 0.28s ease",
      }}
    >
      <div style={{
        width: 50,
        height: 50,
        borderRadius: 14,
        background: muted
          ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
          : "linear-gradient(135deg, #0F9599 0%, #0E4B4D 100%)",
        boxShadow: muted
          ? "0 8px 16px rgba(107, 114, 128, 0.18)"
          : "0 8px 16px rgba(15, 149, 153, 0.22)",
        display: "grid",
        placeItems: "center",
        marginBottom: 18,
      }}>
        <Icon size={22} color="#FFFFFF" strokeWidth={2} />
      </div>
      <div style={{ fontSize: 16.5, fontWeight: 700, color: TEXT_INK, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{feature.title}</div>
      {feature.note && (
        <div style={{ fontSize: 13, color: TEXT_DARK, marginTop: 8, lineHeight: 1.55 }}>{feature.note}</div>
      )}
      {feature.comingSoon && (
        <span style={{
          position: "absolute",
          top: 16,
          right: 16,
          padding: "3px 9px",
          background: "#FBEAD2",
          color: "#9A6212",
          border: "0.5px solid rgba(154, 98, 18, 0.25)",
          borderRadius: 999,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}>
          Coming soon
        </span>
      )}
    </div>
  );
}

export default function FeatureGrid() {
  const features: Feature[] = [
    { icon: FileText, title: "Pay stubs and history" },
    { icon: TrendingUp, title: "Year to date earnings" },
    { icon: Receipt, title: "Year end tax documents", note: "The right year-end form for their country (such as T4 or W-2) to view and download." },
    { icon: Landmark, title: "Direct deposit" },
    { icon: Timer, title: "Clock in and out" },
    { icon: Umbrella, title: "Time off" },
    { icon: Calendar, title: "Schedule", comingSoon: true },
    { icon: UserCheck, title: "Self setup and profile" },
    { icon: Folder, title: "Shared documents", comingSoon: true },
    { icon: Bell, title: "Notifications" },
    { icon: Shield, title: "Secure, role based access" },
    { icon: Smartphone, title: "Web and mobile" },
  ];
  return (
    <section style={{ background: "linear-gradient(180deg, #FFFFFF 0%, " + BG_SOFT + " 100%)", padding: "100px 0" }}>
      <div style={CONTAINER}>
        <div style={{ textAlign: "center", maxWidth: 740, margin: "0 auto 50px" }}>
          <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND, marginBottom: 12 }}>One portal, everything they need</span>
          <h2 style={{ fontSize: "clamp(32px, 4vw, 42px)", fontWeight: 800, color: TEXT_INK, letterSpacing: "-0.02em", lineHeight: 1.1, margin: "0 0 18px" }}>
            All the pay and time tools, in one place
          </h2>
          <p style={{ fontSize: 16, color: TEXT_DARK, lineHeight: 1.6, margin: 0 }}>
            Your team gets the full set of self serve tools, with tax forms and rules that match the country they work in.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {features.map((f, i) => <FeatureCard key={i} feature={f} />)}
        </div>
      </div>
    </section>
  );
}